import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Send, ChevronDown, ChevronUp } from "lucide-react";
import { listGuestbook, addGuestbookEntry } from "@/lib/api";

const PAGE_SIZE = 5;
const ROTATE_MS = 10000;

export default function Guestbook({ invitationId, enabled, preview = false, title }) {
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      try {
        const r = await listGuestbook(invitationId);
        setEntries(r);
      } catch { /* ignore */ }
    })();
  }, [invitationId, enabled]);

  // Rotate the visible window every ROTATE_MS while collapsed.
  const totalPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  useEffect(() => {
    if (expanded || totalPages <= 1) return;
    const id = setInterval(() => setPage((p) => (p + 1) % totalPages), ROTATE_MS);
    return () => clearInterval(id);
  }, [expanded, totalPages]);

  // Keep page valid if entries list shrinks/grows.
  useEffect(() => {
    if (page >= totalPages) setPage(0);
  }, [page, totalPages]);

  const visible = useMemo(() => {
    if (expanded) return entries;
    const start = page * PAGE_SIZE;
    return entries.slice(start, start + PAGE_SIZE);
  }, [entries, page, expanded]);

  if (!enabled) return null;

  const submit = async () => {
    if (preview) {
      toast.info("Vista previa: los recuerdos solo se guardan en la invitación real");
      return;
    }
    if (!name.trim() || !msg.trim()) {
      toast.error("Nombre y mensaje son requeridos");
      return;
    }
    try {
      setLoading(true);
      const r = await addGuestbookEntry(invitationId, { guest_name: name.trim(), message: msg.trim() });
      setEntries((p) => [r, ...p]);
      setName("");
      setMsg("");
      setPage(0);
      toast.success("¡Gracias por tus palabras!");
    } catch {
      toast.error("No se pudo enviar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-14 px-6">
      <div className="text-center mb-8">
        <BookOpen size={20} className="text-warmgold mx-auto" />
        <p className="overline mt-3">Libro de recuerdos</p>
        <h3 className="headline text-4xl text-ink mt-2" data-testid="guestbook-title">{title?.trim() || "Déjanos un mensaje"}</h3>
      </div>
      <div className="max-w-md mx-auto glass rounded-3xl p-6">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-inksoft">Tu nombre</span>
          <input
            data-testid="guestbook-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink"
          />
        </label>
        <label className="block mt-4">
          <span className="text-xs uppercase tracking-widest text-inksoft">Tu mensaje</span>
          <textarea
            data-testid="guestbook-message"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={3}
            className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink resize-none"
          />
        </label>
        <button onClick={submit} disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="guestbook-submit">
          <Send size={14} /> Enviar mensaje
        </button>
      </div>

      {entries.length > 0 && (
        <div className="max-w-md mx-auto mt-8" data-testid="guestbook-list">
          <AnimatePresence mode="wait">
            <motion.div
              key={expanded ? "all" : `page-${page}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
              className="space-y-3"
            >
              {visible.map((e) => (
                <div key={e.id} className="bg-white/60 rounded-2xl p-4 border border-warmgold/20">
                  <p className="script text-warmgold text-lg leading-none">{e.guest_name}</p>
                  <p className="text-inksoft text-sm mt-1 leading-relaxed">{e.message}</p>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          {entries.length > PAGE_SIZE && (
            <div className="mt-5 flex flex-col items-center gap-2">
              {!expanded && totalPages > 1 && (
                <div className="flex gap-1.5" aria-hidden data-testid="guestbook-dots">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${i === page ? "w-6 bg-warmgold" : "w-1.5 bg-warmgold/30"}`}
                    />
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex items-center gap-2 text-inksoft text-xs uppercase tracking-widest hover:text-warmgold transition-colors py-2"
                data-testid="guestbook-toggle"
              >
                {expanded ? (
                  <>Ocultar mensajes <ChevronUp size={14} /></>
                ) : (
                  <>Ver los {entries.length} mensajes <ChevronDown size={14} /></>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
